import { LoadingOutlined } from "@ant-design/icons";
import {
  Table as AntDTable,
  Empty,
  PaginationProps,
  Skeleton,
  SpinProps,
  TablePaginationConfig,
  TableProps,
} from "antd";
import { t } from "i18next";
import { useEffect, useMemo, useState } from "react";
import {
  getTableScrollHeight,
  setTableHeight,
} from "../../utils/getTableScrollHeight";
import { useScreenResize } from "../../utils/useScreenResize";
import "./Table.scss";

export interface Props<RecordType = unknown> extends TableProps<RecordType> {
  name: string;
  scrollConfig?: string[];
  overrideHeight?: string;
  dataUpdatedAt?: number;
}

export const Table = <RecordType extends object>(props: Props<RecordType>) => {
  const viewport = useScreenResize();
  const [currentPage, setCurrentPage] = useState<number | undefined>(1);
  const [currentPageSize, setCurrentPageSize] = useState<number | undefined>(
    (props.pagination as TablePaginationConfig)?.pageSize || 10
  );

  const locale = {
    emptyText: props.loading ? (
      <Skeleton paragraph={{ rows: 8 }} active />
    ) : (
      <Empty
        description={t("COMMON.NEMA_PODATAKA")}
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    ),
  };

  const pagination: PaginationProps = {
    current: currentPage,
    pageSize: currentPageSize,
    locale: { items_per_page: `/ ${t("TABLES.STRANI")} ` },
    ...props.pagination,
    onChange: (pageNumber: number, pageSize: number) => {
      setCurrentPage(pageNumber);
      setCurrentPageSize(pageSize);

      if (props.pagination) {
        (props.pagination as TablePaginationConfig)?.onChange?.(
          pageNumber,
          pageSize
        );
      }
    },
    showSizeChanger: true,
    showTotal: (total: number) => (
      <span>
        {t("TABLES.UKUPAN_BROJ_REZULTATA_PRETRAGE")}: {total}
      </span>
    ),
  };

  const scroll = useMemo(() => {
    return {
      y: `calc(100vh - ${getTableScrollHeight(props.scrollConfig)}px`,
      ...props.scroll,
    };
  }, [props.scrollConfig, props.scroll]);

  const loading = useMemo(() => {
    if (typeof props.loading !== "boolean" && !props.loading) {
      return undefined;
    }

    return {
      size: "large",
      indicator: <LoadingOutlined spin />,
      tip: t("COMMON.UCITAVANJE"),
      ...(typeof props.loading === "boolean"
        ? { spinning: props.loading }
        : props.loading),
    } as boolean | SpinProps | undefined;
  }, [props.loading]);

  useEffect(() => {
    setTableHeight(props.scrollConfig, props.overrideHeight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewport]);

  useEffect(() => {
    // NOTE: AntDesign wont reset pagination when table is rerendered so it has to be done manually
    setCurrentPage(1);
  }, [props.dataUpdatedAt]);

  useEffect(() => {
    setTableHeight(props.scrollConfig, props.overrideHeight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AntDTable
      sticky={true}
      locale={locale}
      data-test={`table-${props.name}`}
      {...props}
      scroll={scroll}
      pagination={
        typeof props.pagination === "boolean" ? props.pagination : pagination
      }
      loading={loading}
    />
  );
};
